import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ServiceRegistrationsActions from './service-registrations.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { filter, forkJoin, mergeMap, Observable, of, tap } from 'rxjs';
import { ServiceRegistrationService } from '../../service/service-registration.service';
import { IOpportunity, OpportunityStatus } from "../../../shared/model/opportunity.model";
import { Action, Store } from "@ngrx/store";
import {
  IServiceRegistration,
  ServiceEvalStatus,
  ServiceStatus
} from "../../../shared/model/service-registration.model";
import { IPaginatedData } from "../../../shared/model/paginated-data.model";
import { IServiceRegistrationSearchFilter } from "../../../shared/model/service-registration-search-filter.model";
import { MessageService } from "../../service/message.service";
import {
  addDeniedUserRecordAction,
  addVolunteerRecordAction,
  removeRegistrationRequestRecordAction, updateRegistrationRequestRecordAction, updateVolunteerRecordAction
} from "../opportunity-config/opportunity-config.actions";
import { refreshSupervisorTableStateTablesForSameOpportunityAction } from "../table/table.actions";
import { OppConfigRecordType } from "../../service/swap-supervisor.service";

@Injectable()
export class ServiceRegistrationsEffects {

  private readonly LIMIT: string = '1000';

  constructor(
    private actions$: Actions,
    private serviceRegistrationService: ServiceRegistrationService,
    private messageService: MessageService,
    private store: Store) { }

  findServiceRegistrations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.findServiceRegistrations),
      switchMap(({ searchFilter, opportunities }: { searchFilter: IServiceRegistrationSearchFilter; opportunities?: IOpportunity[] }) =>
        this.serviceRegistrationService.findServiceRegistration(searchFilter).pipe(
          map((paginatedData: IPaginatedData<IServiceRegistration>) =>
            ServiceRegistrationsActions.findServiceRegistrationsSuccess({
              registrations: paginatedData.data,
              opportunityIds: opportunities?.map((opportunity: IOpportunity): string => opportunity.id) ?? []
            })
          ),
          catchError((error: any) =>
            of(ServiceRegistrationsActions.findServiceRegistrationsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  findServiceRegistrationsForOpportunitiesByStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.findServiceRegistrationsForOpportunitiesByStatus),
      map((action: { supervisorId: string; opportunities: IOpportunity[]; status: OpportunityStatus }) => {
        const supervisorId = action.supervisorId;
        const opportunityIds = action.opportunities
          .filter((opportunity: IOpportunity) => opportunity.status === action.status)
          .map((opportunity: IOpportunity) => opportunity.id);
        return { supervisorId, opportunityIds };
      }),
      filter(({ supervisorId, opportunityIds }) => !!supervisorId && opportunityIds.length > 0),
      switchMap(({ supervisorId, opportunityIds }) =>
        forkJoin({
          pending: this._findPendingRegistrations(supervisorId),
          registered: this._findRegisteredRegistrations(supervisorId)
        }).pipe(
          map(({ pending, registered }) =>
            ServiceRegistrationsActions.findServiceRegistrationsSuccess({
              registrations: [...pending, ...registered],
              opportunityIds
            })
          ),
          catchError((error: any) =>
            of(ServiceRegistrationsActions.findServiceRegistrationsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  findServiceRegistrationsWithOpportunityIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.findServiceRegistrationsWithOpportunityIds),
      switchMap(({ searchFilter, opportunityIds }) =>
        this._findRegistrations(searchFilter).pipe(
          map((registrations: IServiceRegistration[]) =>
            ServiceRegistrationsActions.findServiceRegistrationsSuccess({
              registrations,
              opportunityIds
            })
          ),
          catchError((error: any) =>
            of(ServiceRegistrationsActions.findServiceRegistrationsFailure({
              error: error.message
            }))
          )
        )
      )
    )
  );

  updateRegistrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrations),
      switchMap(({ registrations, noMessage }) =>
        forkJoin(
          registrations.map(registration =>
            this.serviceRegistrationService.updateServiceRegistration(registration).pipe(
              map((updated: IServiceRegistration) => updated),
              catchError(() => of(null))
            )
          )
        ).pipe(
          map((results) => {
            const updated = results.filter(r => !!r) as IServiceRegistration[];
            return ServiceRegistrationsActions.updateServiceRegistrationsSuccess({ registrations: updated, noMessage });
          }),
          catchError(error =>
            of(ServiceRegistrationsActions.updateServiceRegistrationsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  updateRegistrationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrationsSuccess),
      tap(({ registrations, noMessage }) => {
        registrations.forEach(registration => this._handleRegistrationUpdate(registration));
        if (!noMessage) this._showUpdateMessage(registrations);
      })
    ),
    { dispatch: false }
  );

  updateRegistrationsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrationsFailure),
      tap(({ error }) => {
        this.messageService.showMessage(`Failed to update registrations: ${error}`);
      })
    ),
    { dispatch: false }
  );

  updateServiceRegistrationWithSupervisor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrationWithSupervisor),
      switchMap(({ registration, supervisor, recordType }) =>
        this.serviceRegistrationService.updateServiceRegistration(registration).pipe(
          map(updatedRegistration => {
            const actions: Action[] = [
              ServiceRegistrationsActions.updateServiceRegistrationWithSupervisorSuccess({
                registration: updatedRegistration,
                supervisor,
                recordType
              })
            ];
            if (recordType === OppConfigRecordType.regReq) {
              actions.push(updateRegistrationRequestRecordAction({ record: updatedRegistration }));
              actions.push(refreshSupervisorTableStateTablesForSameOpportunityAction());
            } else if (recordType === OppConfigRecordType.activeVolunteer) {
              actions.push(updateVolunteerRecordAction({ record: updatedRegistration }));
              actions.push(refreshSupervisorTableStateTablesForSameOpportunityAction());
            }
            return actions;
          }),
          catchError(error =>
            of([
              ServiceRegistrationsActions.updateServiceRegistrationWithSupervisorFailure({
                error,
                supervisor,
                recordType
              })
            ])
          )
        )
      ),
      mergeMap(actions => actions)
    )
  );

  updateServiceRegistrationWithSupervisorSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrationWithSupervisorSuccess),
      tap(({ registration, supervisor }) => {
        this._showUpdateMessage(
          [registration],
          `Supervisor swapped with ${supervisor.firstName} ${supervisor.lastName}`
        );
      })
    ),
    { dispatch: false }
  );

  updateServiceRegistrationWithSupervisorFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRegistrationsActions.updateServiceRegistrationWithSupervisorFailure),
      tap(({ supervisor }) => {
        this._showUpdateMessage(
          [],
          `Unable to swap supervisor with ${supervisor.firstName} ${supervisor.lastName}. Please contact the LU Serve office for assistance.`
        );
      })
    ),
    { dispatch: false }
  );

  private _buildPendingParams(supervisorId: string): IServiceRegistrationSearchFilter {
    return {
      supervisorId,
      serviceStatuses: [ServiceStatus.PENDING],
      limit: this.LIMIT,
      offset: '0'
    };
  }

  private _buildRegisteredParams(supervisorId: string): IServiceRegistrationSearchFilter {
    return {
      supervisorId,
      serviceStatuses: [ServiceStatus.REGISTERED],
      studentEvalStatus: ServiceEvalStatus.COMPLETE,
      supervisorEvalStatus: [ServiceEvalStatus.INCOMPLETE, ServiceEvalStatus.REJECTED],
      limit: this.LIMIT,
      offset: '0'
    };
  }

  private _findRegistrations(searchFilter: IServiceRegistrationSearchFilter): Observable<IServiceRegistration[]> {
    return this.serviceRegistrationService
      .findServiceRegistration(searchFilter)
      .pipe(map((page: IPaginatedData<IServiceRegistration>): IServiceRegistration[] => page.data));
  }

  private _findPendingRegistrations(supervisorId: string): Observable<IServiceRegistration[]> {
    return this._findRegistrations(this._buildPendingParams(supervisorId));
  }

  private _findRegisteredRegistrations(supervisorId: string): Observable<IServiceRegistration[]> {
    return this._findRegistrations(this._buildRegisteredParams(supervisorId));
  }

  private _handleRegistrationUpdate(registration: IServiceRegistration): void {
    this.store.dispatch(removeRegistrationRequestRecordAction({ record: registration }));
    if (registration.status === 'REGISTERED') {
      this.store.dispatch(addVolunteerRecordAction({ record: registration }));
    } else {
      this.store.dispatch(addDeniedUserRecordAction({ record: registration }));
    }
    this.store.dispatch(refreshSupervisorTableStateTablesForSameOpportunityAction());
  }

  private _showUpdateMessage(registrations: IServiceRegistration[], customMessage?: string): void {
    if (customMessage) {
      this.messageService.showMessage(customMessage);
      return;
    }

    let message: string | null = null;

    // Set the message for multiple registrations
    if (registrations.length > 1) {
      const firstRegistrationStatus: string = registrations[0].status;
      const allRegistrationsHaveSameStatus: boolean = registrations.every(
        (serviceRegistration: IServiceRegistration): boolean => serviceRegistration.status === firstRegistrationStatus);

      if (allRegistrationsHaveSameStatus) {
        switch (firstRegistrationStatus) {
          case 'DENIED': message = 'All selected registration requests were denied'; break;
          case 'REGISTERED': message = 'All selected registration requests were registered'; break;
        }
      }
    }

    // Set the message for a single registration
    else if (registrations.length === 1) {
      const serviceRegistration: IServiceRegistration = registrations[0];
      switch (serviceRegistration.status) {
        case 'DENIED':
          message = `${serviceRegistration.studentName}'s registration request for ${serviceRegistration.oppName} was denied`;
          break;
        case 'REGISTERED':
          message = `${serviceRegistration.studentName} has been registered for ${serviceRegistration.oppName}`;
          break;
      }
    }

    if (message) this.messageService.showMessage(message);
  }

}
